import { dashboardObservability } from "./observability";
import { defineEvent as defineOperationalEvent } from "@diverge/observability/browser";
const operationalLogger = dashboardObservability.logger("dashboard/utils/apiStorage");
const operationalEvents = {
  event1: defineOperationalEvent({
    name: "dashboard.utils.api_storage.failed_to_load_admin_chatbot_ids.28aeab02",
    message: "Failed to load admin chatbot IDs:",
    level: "error",
    attributes: { "di.reason_code": { type: "category" } },
    classification: "operational",
    volume: "boundary",
    owner: "dashboard/src/utils/apiStorage",
  }),
};
import type { ChatbotSelection, UserSettings } from "../types";
import api from "./apiClient";

const ACCOUNT_SCOPED_STORAGE_KEYS = [
  "accessible_user_ids",
  "agent_proposals_agent_filter",
  "agent_name",
  "chatbot_ids",
  "chatbot_preview_state",
  "company_info",
  "is_admin",
  "is_authenticated",
  "is_limited_admin",
  "lastClickedChatbotOrder",
  "ligegyldig_visible",
  // Browser cache of the per-user livechat chatbot filter (#586); the next
  // sign-in hydrates it from the server, so it must not leak across accounts.
  "livechat_chatbot_filter",
  "livechat_environment",
  "livechat_translation_languages",
  "organizations",
  "pending_emne_filter",
  "pending_topic_filter",
  "pending_fallback_filter",
  "pending_resolution_category_filter",
  "pending_fejlstatus_filter",
  "pending_filter_timestamp",
  "pending_purchase_filter",
  "profile_picture",
  "role",
  "selected_chatbot_id",
  "thumbs_rating",
];

function parsePostgresStyleArray(trimmed: string): string[] {
  const content = trimmed.slice(1, -1);
  const result: string[] = [];
  let currentItem = "";
  let inQuotes = false;
  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];
    if (char === '"' && (i === 0 || content[i - 1] !== "\\")) {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === "," && !inQuotes) {
      const normalized = currentItem.trim();
      if (normalized !== "") result.push(normalized);
      currentItem = "";
      continue;
    }
    currentItem += char;
  }
  const normalized = currentItem.trim();
  if (normalized !== "") result.push(normalized);
  return result;
}

function parseStoredArrayValue(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (trimmed === "") return [];
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    return parsePostgresStyleArray(trimmed);
  }
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [trimmed];
}

function normalizeStringArray(value: unknown): string[] {
  return parseStoredArrayValue(value)
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);
}

interface ChatbotSettingsClient {
  get(url: string): Promise<{ status: number; data: unknown }>;
}

export async function hydrateAdminChatbotIds(
  settings: UserSettings | null,
  client: ChatbotSettingsClient = api,
): Promise<UserSettings | null> {
  if (settings?.isAdmin !== true) return settings;
  try {
    const response = await client.get("/api/v1/chatbot-settings");
    return response.status === 200
      ? { ...settings, chatbot_ids: normalizeStringArray(response.data) }
      : settings;
  } catch (error) {
    operationalLogger.error(operationalEvents.event1, error, {
      "di.reason_code": "failed_to_load_admin_chatbot_ids",
    });
    return settings;
  }
}

export function clearLocalAuthState(): void {
  ACCOUNT_SCOPED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function readStoredArray(key: string): unknown[] {
  try {
    return parseStoredArrayValue(localStorage.getItem(key));
  } catch {
    return [];
  }
}

export function readStoredStringArray(key: string): string[] {
  return readStoredArray(key)
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);
}

export function resolveChatbotSelection(
  chatbotIdsInput: unknown,
  selectedChatbotIdInput: unknown,
): ChatbotSelection {
  const chatbotIds = normalizeStringArray(chatbotIdsInput);
  const selectedChatbotId =
    typeof selectedChatbotIdInput === "string" ? selectedChatbotIdInput.trim() : "";
  if (chatbotIds.length === 0) {
    return {
      primaryChatbotId: null,
      requestedChatbotId: null,
      requestedChatbotIds: [],
      selectedChatbotId: null,
      usesAll: false,
    };
  }
  if (chatbotIds.length === 1) {
    const chatbotId = chatbotIds[0];
    return {
      primaryChatbotId: chatbotId,
      requestedChatbotId: chatbotId,
      requestedChatbotIds: [chatbotId],
      selectedChatbotId: chatbotId,
      usesAll: false,
    };
  }
  if (
    selectedChatbotId !== "" &&
    selectedChatbotId !== "ALL" &&
    chatbotIds.includes(selectedChatbotId)
  ) {
    return {
      primaryChatbotId: selectedChatbotId,
      requestedChatbotId: selectedChatbotId,
      requestedChatbotIds: [selectedChatbotId],
      selectedChatbotId,
      usesAll: false,
    };
  }
  return {
    primaryChatbotId: chatbotIds[0] ?? null,
    requestedChatbotId: chatbotIds.join(","),
    requestedChatbotIds: chatbotIds,
    selectedChatbotId: "ALL",
    usesAll: true,
  };
}

export function persistAuthSessionState(settings: UserSettings | null | undefined): void {
  const chatbotIds = normalizeStringArray(settings?.chatbot_ids);
  const selection = resolveChatbotSelection(
    chatbotIds,
    localStorage.getItem("selected_chatbot_id"),
  );
  const memberships = Array.isArray(settings?.memberships) ? settings.memberships : [];
  const organizationNames =
    Array.isArray(settings?.organizations) && settings.organizations.length > 0
      ? settings.organizations
      : memberships
          .map((membership) => membership?.organizationName)
          .filter((name) => typeof name === "string" && name);
  localStorage.setItem("organizations", JSON.stringify(organizationNames));
  localStorage.setItem("chatbot_ids", JSON.stringify(chatbotIds));
  if (selection.selectedChatbotId) {
    localStorage.setItem("selected_chatbot_id", selection.selectedChatbotId);
  } else {
    localStorage.removeItem("selected_chatbot_id");
  }
  localStorage.setItem("thumbs_rating", settings?.thumbs_rating ? "true" : "false");
  localStorage.setItem("role", settings?.role || "");
  localStorage.setItem("company_info", settings?.company_info || "");
  localStorage.setItem("agent_name", settings?.agent_name || "Support Agent");
  localStorage.setItem("profile_picture", settings?.profile_picture || "");
}
